/**
 * RCD-3 — Recharts dimension-warning console filter.
 *
 * Recharts emits this warning whenever its container reports 0 / -1 size:
 *
 *   "The width(-1) and height(-1) of chart should be greater than 0, …"
 *
 * `SafeResponsiveContainer`'s first-paint gate (RCD-2) prevents this for
 * our own callsites, but lazy-loaded chunks, third-party charts, and
 * print-mode pre-mount passes can still emit it before the gate runs.
 * The console floods (128+ per Site Evidence load on 2026-05-18) drown
 * out real errors, so we drop the exact dimension-warning pattern at
 * the console-interception layer.
 *
 * Tightly scoped — the regex matches ONLY recharts' specific phrasing.
 * Any other warning (including other recharts warnings) is passed through.
 * Suppressed count is exposed on `globalThis.__rcdSuppressedCount` so the
 * admin diagnostic chip (deferred) can confirm the count trends to zero
 * as more callsites adopt SafeResponsiveContainer.
 *
 * Install ONCE at app boot — repeat installs are guarded by a sentinel
 * to keep test re-mounts and HMR cycles from stacking the wrapper.
 */

const INSTALLED = Symbol.for('deepatomic.rcdFilterInstalled');
const RECHARTS_DIMENSION_WARNING = /width\(-1\) and height\(-1\) of chart/;

interface RcdGlobals {
  [INSTALLED]?: boolean;
  __rcdSuppressedCount?: number;
}

/**
 * Install the recharts dimension-warning filter on the global console.
 * Returns the prior console.warn handler so callers (tests) can restore
 * it manually if needed.
 */
export function installRechartsWarnFilter(target: { console: Console } = globalThis as unknown as { console: Console }): () => void {
  const g = target as unknown as RcdGlobals;
  if (g[INSTALLED]) {
    return () => undefined;
  }
  const originalWarn = target.console.warn.bind(target.console);

  target.console.warn = function rcdFilteredWarn(...args: unknown[]) {
    const first = args[0];
    if (typeof first === 'string' && RECHARTS_DIMENSION_WARNING.test(first)) {
      g.__rcdSuppressedCount = (g.__rcdSuppressedCount ?? 0) + 1;
      return;
    }
    return originalWarn(...args);
  };
  g[INSTALLED] = true;

  // Uninstall hook — primarily for tests; production never tears down.
  return () => {
    target.console.warn = originalWarn;
    g[INSTALLED] = false;
  };
}
